import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

/** Item con id, respuesta correcta y texto. Usado por JK_OFFICE_ADMIN, JK_SALES_RETAIL, etc. */
export interface TrueFalseNoSeItem {
  id: string;
  correctAnswer: 'V' | 'F';
  text: string;
}

export type TrueFalseNoSeAnswer = 'V' | 'F' | 'N';

export interface TrueFalseNoSeRunnerProps {
  commercialName: string;
  items: TrueFalseNoSeItem[];
  onComplete: (answers: Record<string, TrueFalseNoSeAnswer>, itemsPresented: string[]) => void;
  initialAnswers?: Record<string, TrueFalseNoSeAnswer>;
}

const OPTIONS: { value: TrueFalseNoSeAnswer; label: string }[] = [
  { value: 'V', label: 'Verdadero' },
  { value: 'F', label: 'Falso' },
  { value: 'N', label: 'No sé' },
];

export function TrueFalseNoSeRunner({
  commercialName,
  items,
  onComplete,
  initialAnswers = {},
}: TrueFalseNoSeRunnerProps) {
  const [answers, setAnswers] = useState<Record<string, TrueFalseNoSeAnswer>>(initialAnswers);
  const [currentIndex, setCurrentIndex] = useState(0);

  const setAnswer = useCallback((itemId: string, value: TrueFalseNoSeAnswer) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  const answered = items.filter((i) => answers[i.id] != null).length;
  const allComplete = answered === items.length;

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (allComplete) {
      onComplete(answers, items.map((i) => i.id));
    }
  };

  const handleSubmit = () => {
    if (!allComplete) return;
    onComplete(answers, items.map((i) => i.id));
  };

  const item = items[currentIndex];
  const choice = item ? answers[item.id] : undefined;

  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pregunta {currentIndex + 1} de {items.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {currentIndex === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            Indica si cada afirmación es Verdadera o Falsa según tu conocimiento. Si no estás seguro, puedes elegir "No sé".
          </p>
        )}
        <p className="font-medium">{item.text}</p>
        <div className="flex flex-col gap-2">
          {OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
            >
              <input
                type="radio"
                name={item.id}
                checked={choice === opt.value}
                onChange={() => setAnswer(item.id, opt.value)}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            Anterior
          </Button>
          {currentIndex < items.length - 1 ? (
            <Button type="button" onClick={handleNext} disabled={!choice}>
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!allComplete}>
              Finalizar prueba
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Respondidas: {answered} / {items.length}
        </p>
      </CardContent>
    </Card>
  );
}

/** Alias para compatibilidad con JK_OFFICE_ADMIN. */
export const JKOfficeAdminRunner = TrueFalseNoSeRunner;
